import SubmitButton from "Components/Buttons/SubmitButton";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addBrands,
  fetchBrandsById,
  updateBrands,
} from "slices/attributes/thunk";
import * as Yup from "yup";

const AttributeFForm: React.FC = () => {
  const location = useLocation();
  const brandData = location.state?.brand;
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [initialData, setInitialData] = useState({
    attribute_name: "",
  });

  const dispatch = useDispatch<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isEdit) {
      if (brandData) {
        setInitialData({ attribute_name: brandData.attribute_name || "" });
      } else if (id) {
        // If state not available, fetch from API
        setIsLoading(true);
        dispatch(fetchBrandsById(id))
          .then((res: any) => {
            setInitialData({
              attribute_name: res?.payload?.attribute_name || "",
            });
          })
          .catch(() => {
            toast.error("Failed to load attribute details");
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [id, isEdit, brandData, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData,
 validationSchema: Yup.object({
  attribute_name: Yup.string()
    .trim()
    .required("Please enter attribute name")
    .min(2, "Attribute name must be at least 2 characters")
    .max(50, "Attribute name must not exceed 50 characters")
    .matches(
      /^[A-Za-z][A-Za-z0-9\s]*$/,
      "Attribute name must start with a letter and contain only letters, numbers, and spaces"
    )
    .test('no-random-chars', 'Please enter a valid attribute name', (value) => {
      if (!value) return false;
      // Check if it's not just random characters
      const hasVowels = /[aeiouAEIOU]/.test(value);
      const hasConsonants = /[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]/.test(value);
      return hasVowels && hasConsonants;
    })
}),
    onSubmit: async (values) => {
      const payload = {
        attribute_name: values.attribute_name.trim(),
      };
      console.log("Submitting payload:", payload);

      try {
        if (isEdit && id) {
          await dispatch(updateBrands({ id, payload })).unwrap();
          toast.success("Attribute updated successfully");
        } else {
          await dispatch(addBrands(payload)).unwrap();
          toast.success("Attribute added successfully");
        }
        navigate(-1)
      } catch (error: any) {
        console.error("Attribute save error:", error);

        if (error?.errors?.attribute_name) {
          const msg = error.errors.attribute_name[0];
          formik.setFieldError("attribute_name", msg);
          toast.error(msg);
        } else {
          toast.error(error?.message || "Failed to save attribute");
        }
      }
    },
  });

  return (
    <div className="page-content px-4">
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0 0 15px rgba(0,0,0,0.05)",
        }}
      >
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                {/* Left: Back icon and heading */}
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{ fontSize: "24px", cursor: "pointer" }}
                    onClick={() => navigate(-1)}
                  >
                    ←
                  </span>
                  <h5 className="mb-0 fw-bold">
                    {isEdit ? "Edit Attributes" : "Add Attributes"}
                  </h5>
                </div>

                {/* Right: Breadcrumb */}
                <div   className="text-muted" style={{ fontSize: "0.9rem" ,cursor:"pointer"}}>
                  <span
                    style={{
                      color: "inherit",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(-1)}
                  >
                    Attributes
                  </span>{" "}
                  &gt;{" "}
                  <span
                     className="text-muted" style={{ fontSize: "0.9rem" ,cursor:"pointer"}}
                  >
                    {isEdit ? "Edit Attributes" : "Add Attributes"}
                  </span>{" "}
                </div>
              </div>
              <Col lg={12}>
                <div className="mb-3">
                  <Form.Label>
                    Attributes Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="attribute_name"
                    placeholder="Enter Attributes Name"
                    value={formik.values.attribute_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.attribute_name &&
                      formik.touched.attribute_name
                    }
                    style={{
                      height: "40px",
                      lineHeight: "1.5",
                      fontSize: "0.875rem",
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.attribute_name}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 py-3">
              <Button
                variant="outline-secondary"
                style={{
                  color: "#878899",
                  borderColor: "#B0BAC9",
                  backgroundColor: "transparent",
                }}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>

              {/* <Button
                type="submit"
                className="btn"
                style={{
                  backgroundColor: "#0073BB",
                  borderColor: "#0073BB",
                  color: "#fff",
                }}
              >
                {isEdit ? "Update" : "Add"}
              </Button> */}

              {/* Reusable submit button with spinner */}
              <SubmitButton
                isSubmitting={formik.isSubmitting}
                isEdit={isEdit}
              />
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default AttributeFForm;